'use client'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import jump from 'jump.js'
import { cn, getLocalizedPath } from '../../helpers/index.js'
import type { Media as MediaType, Theme } from '../../types/props.js'
import { Text, Button, Heading, Picture, Countdown } from '../../components/index.js'
import Media from '../Media/index.js'
import type { ButtonProps } from '../../components/button.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { withLayout } from '../../shared/Styles.js'

export type SecondaryBannerSemanticName =
  | 'root'
  | 'title'
  | 'superTitle'
  | 'subtitle'
  | 'content'
  | 'buttonGroup'
  | 'primaryButton'
  | 'secondaryButton'

/**
 * jump.js 配置选项
 */
export interface JumpOptions {
  /** 动画持续时间（毫秒） @default 500 */
  duration?: number
  /** 偏移量（像素） @default 0 */
  offset?: number
  /** 动画完成回调 */
  callback?: () => void
  /** 缓动函数 */
  easing?: (t: number, b: number, c: number, d: number) => number
  /** 是否考虑 a11y @default false */
  a11y?: boolean
}

const secondaryBannerVariants = cva(
  // 基础样式
  'text-info-primary relative w-full',
  {
    variants: {
      size: {
        sm: 'tablet:aspect-[768/480] laptop:aspect-[1024/400] desktop:aspect-[1440/384] lg-desktop:aspect-[1920/480] aspect-[390/480]',
        base: 'tablet:aspect-[768/660] laptop:aspect-[1024/432] desktop:aspect-[1440/576] lg-desktop:aspect-[1920/720] aspect-[390/660]',
        lg: 'tablet:aspect-[768/660] laptop:aspect-[1024/520] desktop:aspect-[1440/700] lg-desktop:aspect-[1920/930] aspect-[390/660]',
        xlg: 'tablet:aspect-[768/660] laptop:aspect-[1024/520] desktop:aspect-[1440/700] lg-desktop:aspect-[1920/930] aspect-[390/660] overflow-visible',
      },
    },
    defaultVariants: {
      size: 'base',
    },
  }
)

/**
 * SecondaryBanner 业务组件数据接口
 */
export interface SecondaryBannerData {
  /** 主标题*/
  title?: string
  /**
   * 主标题标签
   * @default 'h1'
   */
  titleTag?: 'h1' | 'h2'
  /**
   * 上标题/引导标题
   * 通常用于分类、引导性文字
   */
  superTitle?: string
  /**
   * 副标题/描述文字
   * 通常用于补充说明、详细描述
   */
  subtitle?: string
  /**
   * 尺寸:
   *  sm: 小尺寸用于筛选页面
   *  base: 基础尺寸用于
   *  lg: 大尺寸用于专题页
   *  xlg: 活动页（通栏）
   */
  size?: 'sm' | 'base' | 'lg' | 'xlg'
  pcImage?: MediaType
  padImage?: MediaType
  mobileImage?: MediaType
  /** 视频是否循环播放 */
  isVideoLoop?: boolean
  /** 结束时间（ISO 字符串） */
  endDate?: string
  /** 结束时间时区（如: America/Los_Angeles） */
  endDate_tz?: string
  /**
   * 时间格式化配置（JSON 字符串）
   * {"day":"日","hour":"时","minute":"分","second":"秒"}
   */
  dateFormat?: string

  /** 主按钮配置 */
  primaryButton?: {
    text: string
    link?: string
    onClick?: () => void
  } & Omit<ButtonProps, 'children'>
  /** 次级按钮配置 */
  secondaryButton?: {
    text: string
    link?: string
    onClick?: () => void
  } & Omit<ButtonProps, 'children'>

  iconArray?: Array<{ pcImage: MediaType }>
  theme?: Theme
}

export interface SecondaryBannerProps extends React.HTMLAttributes<HTMLElement> {
  /** 业务数据 */
  data: SecondaryBannerData
  classNames?: Partial<Record<SecondaryBannerSemanticName, string>>
  /**
   * 锚点跳转配置（用于 # 开头的链接）
   * @default { duration: 500, offset: 0 }
   */
  jumpOptions?: JumpOptions
}

/**
 * SecondaryBanner - 次级横幅banner
 *
 * @description 是指用户进入二级页面后，无需滚动页面就能在第一屏（即"首屏"）看到的、通常横跨整个浏览器窗口宽度的核心视觉区域。
 */
const SecondaryBanner = React.forwardRef<HTMLElement, SecondaryBannerProps>(
  ({ className, classNames = {}, data, jumpOptions = {}, ...props }, ref) => {
    const {
      pcImage,
      padImage,
      mobileImage,
      isVideoLoop = true,
      endDate,
      endDate_tz,
      dateFormat,
      size = 'base',
      iconArray: icons,
      theme = 'light',
      primaryButton,
      secondaryButton,
      title,
      titleTag = 'h1',
    } = data

    const { locale = 'us' } = useAiuiContext()

    // 默认 jump 配置
    const defaultJumpOptions: JumpOptions = { duration: 500, offset: 0 }
    const mergedJumpOptions = { ...defaultJumpOptions, ...jumpOptions }

    // 生成唯一ID用于可访问性
    const bannerId = React.useId()
    const titleId = title ? `${bannerId}-title` : undefined
    const subtitleId = data.subtitle ? `${bannerId}-subtitle` : undefined
    const superTitleId = data.superTitle ? `${bannerId}-super-title` : undefined

    // 构建aria-describedby
    const describedBy = [superTitleId, subtitleId].filter(Boolean).join(' ') || undefined

    return (
      <section
        ref={ref}
        role="banner"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        className={cn(
          {
            'aiui-dark': theme === 'dark',
          },
          secondaryBannerVariants({ size: size }),
          className,
          classNames?.root
        )}
        {...props}
      >
        <Media
          pcImage={pcImage}
          padImage={padImage}
          mobileImage={mobileImage}
          className={cn('absolute inset-0 size-full object-cover', {
            'h-fit': size === 'xlg',
          })}
          imgClassName="object-cover"
          videoClassName="object-cover"
          muted={true}
          loop={isVideoLoop}
          playsInline={true}
          autoPlay={true}
        />
        <div
          className={cn(
            'banner-content laptop:top-1/2 laptop:-translate-y-1/2 tablet:px-[32px] laptop:px-[64px] lg-desktop:px-[calc(50%-832px)] absolute top-12 z-10 flex flex-col items-start justify-center  px-[16px] text-left',
            classNames?.content
          )}
        >
          <div className="banner-inner-title desktop:gap-2 flex flex-col items-start gap-1">
            {data.superTitle && (
              <Text
                as="p"
                size={2}
                id={superTitleId}
                className={cn('banner-super-title desktop:text-[16px] lg-desktop:text-[18px]', classNames?.superTitle)}
                html={data.superTitle}
              />
            )}

            {data.title && (
              <Heading
                as={titleTag}
                size={['xlg', 'lg'].includes(size) ? 5 : 4}
                id={titleId}
                className={cn('banner-title ', classNames?.title)}
                html={data.title}
              />
            )}

            {data.subtitle && (
              <Text
                as="p"
                size={3}
                id={subtitleId}
                className={cn('banner-subtitle desktop:text-[16px] lg-desktop:text-[18px]', classNames?.subtitle)}
                html={data.subtitle}
              />
            )}
          </div>

          {endDate && (
            <div className="banner-countdown">
              <Countdown
                endDate={endDate}
                endDate_tz={endDate_tz}
                dateFormat={dateFormat}
                variant="spacious"
                className="lg-desktop:mt-8 mt-6"
              />
            </div>
          )}

          <div
            className={cn(
              'banner-cta-group lg-desktop:mt-[32px] lg-desktop:gap-3 mt-6 flex gap-2',
              classNames?.buttonGroup
            )}
          >
            {secondaryButton?.text && (
              <Button
                variant="secondary"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (secondaryButton.link?.startsWith('#')) {
                    e.preventDefault()
                    const element = document.querySelector(secondaryButton.link)
                    if (element) {
                      jump(element as HTMLElement, mergedJumpOptions)
                    }
                  }
                  secondaryButton.onClick?.()
                }}
                className={cn('', classNames?.secondaryButton)}
                aria-describedby={titleId}
                {...(secondaryButton.link && !secondaryButton.link.startsWith('#')
                  ? { as: 'a', href: getLocalizedPath(secondaryButton.link, locale) }
                  : {})}
              >
                {secondaryButton.text}
              </Button>
            )}
            {primaryButton?.text && (
              <Button
                onClick={e => {
                  if (primaryButton.link?.startsWith('#')) {
                    e.preventDefault()
                    const element = document.querySelector(primaryButton.link)
                    if (element) {
                      jump(element as HTMLElement, mergedJumpOptions)
                    }
                  }
                  primaryButton.onClick?.()
                }}
                className={cn('', classNames?.primaryButton)}
                aria-describedby={titleId}
                {...(primaryButton.link && !primaryButton.link.startsWith('#')
                  ? { as: 'a', href: getLocalizedPath(primaryButton.link, locale) }
                  : {})}
              >
                {primaryButton.text}
              </Button>
            )}
          </div>

          {Array.isArray(icons) && icons.length > 0 && (
            <div
              className="banner-icons desktop:mt-3 lg-desktop:mt-4 mt-2 flex items-center gap-2"
              role="group"
              aria-label="brand icons"
            >
              {icons.map((icon, index) =>
                icon.pcImage ? (
                  <Picture
                    key={index}
                    source={icon.pcImage.url}
                    alt={icon.pcImage.alt || `icon ${index + 1}`}
                    role="img"
                    className="laptop:h-auto h-[48px] max-w-none"
                    imgClassName="h-full"
                  />
                ) : null
              )}
            </div>
          )}
        </div>
      </section>
    )
  }
)

SecondaryBanner.displayName = 'SecondaryBanner'
export default withLayout(SecondaryBanner)
